import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, ArrowLeft, ArrowUpRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { getBlogs } from "../api";

export default function BlogDetails() {
  const { id } = useParams();

  const [blogs, setBlogs] = useState([]);
  const [blog, setBlog] = useState(null);

  useEffect(() => {
    getBlogs().then((data) => {
      setBlogs(data);

      const found = data.find(
        (b, index) =>
          String(b.id) === String(id) ||
          String(index) === String(id)
      );

      setBlog(found || null);
    });
  }, [id]);

  if (!blog) {
    return (
      <main className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Blog Not Found
          </h2>

          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 bg-[#ef2435] text-white px-6 py-3 rounded-full"
          >
            <ArrowLeft size={18} />
            Back to Blogs
          </Link>
        </div>
      </main>
    );
  }

  const relatedBlogs = blogs
    .filter((b) => b.id !== blog.id)
    .slice(0, 3);

  return (
    <main>
      <PageHero
        title={blog.title}
        subtitle={blog.excerpt}
        num="08"
      />

      {/* HERO IMAGE */}
      <section className="bg-[#fafafa] pt-14">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="rounded-[36px] overflow-hidden shadow-2xl border border-[#ececef]">
            <img
              src={blog.image || blog.img}
              alt={blog.title}
              className="w-full h-[300px] md:h-[550px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-20 bg-[#fafafa]">
        <div className="max-w-[1000px] mx-auto px-5 md:px-8">

          {/* META */}
          <div className="flex flex-wrap items-center gap-5 mb-10">

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#ececef] text-sm text-gray-700">
              <Calendar size={16} className="text-[#ef2435]" />
              {blog.published_at || blog.date}
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ef2435]/10 text-[#ef2435] text-sm font-semibold">
              SSC READYMIX
            </div>

          </div>

          {/* TITLE */}
          <h1 className="text-4xl md:text-6xl font-black leading-tight text-gray-900 mb-10">
            {blog.title}
          </h1>

          {/* EXCERPT */}
          <div className="rounded-3xl bg-white border border-[#ececef] p-8 md:p-10 mb-10 shadow-sm">
            <p className="text-xl leading-[2] text-gray-700">
              {blog.excerpt}
            </p>
          </div>

          {/* CONTENT */}
          <div className="bg-white rounded-[36px] border border-[#ececef] p-8 md:p-14 shadow-sm">

            <div
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-[2] prose-li:text-gray-700"
              dangerouslySetInnerHTML={{
                __html:
                  blog.content ||
                  `
                  <p>
                    SSC READYMIX continues to deliver engineered concrete solutions
                    with precision, consistency, and reliability across modern
                    construction projects.
                  </p>

                  <p>
                    Through advanced batching systems, strict quality control,
                    efficient logistics, and disciplined execution, we ensure
                    dependable performance from production to placement.
                  </p>

                  <h2>Quality Driven Operations</h2>

                  <p>
                    Every stage of production follows structured operational
                    standards designed to maintain strength, workability,
                    durability, and timely delivery.
                  </p>

                  <h2>Reliable Project Execution</h2>

                  <p>
                    We work closely with engineers, contractors, and developers
                    to support seamless execution across residential,
                    commercial, and infrastructure developments.
                  </p>
                  `,
              }}
            />

          </div>

          {/* BACK BUTTON */}
          <div className="mt-14">
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 bg-[#111] hover:bg-[#ef2435] transition-all text-white px-7 py-4 rounded-full font-semibold"
            >
              <ArrowLeft size={18} />
              Back to Blogs
            </Link>
          </div>

        </div>
      </section>

      {/* RELATED BLOGS */}
      {relatedBlogs.length > 0 && (
        <section className="py-20 bg-white border-t border-[#ececef]">

          <div className="max-w-[1400px] mx-auto px-5 md:px-8">

            <div className="mb-12">
              <span className="section-eyebrow">
                [ RELATED ARTICLES ]
              </span>

              <h2 className="text-5xl md:text-6xl font-bold text-gray-900">
                MORE INSIGHTS.
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">

              {relatedBlogs.map((b, i) => (
                <Link
                  key={i}
                  to={`/blogs/${b.id || i}`}
                  className="group rounded-[30px] overflow-hidden border border-[#ececef] bg-[#fafafa] hover:border-[#ef2435] hover:-translate-y-2 transition-all duration-500"
                >

                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={b.image || b.img}
                      alt={b.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                    />
                  </div>

                  <div className="p-7">

                    <div className="flex items-center justify-between mb-4">

                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar size={12} />
                        {b.published_at || b.date}
                      </div>

                      <ArrowUpRight
                        size={18}
                        className="text-[#ef2435]"
                      />

                    </div>

                    <h3 className="text-2xl font-bold text-gray-900 leading-tight mb-4 group-hover:text-[#ef2435] transition-all">
                      {b.title}
                    </h3>

                    <p className="text-gray-600 leading-relaxed line-clamp-3">
                      {b.excerpt}
                    </p>

                  </div>
                </Link>
              ))}

            </div>

          </div>

        </section>
      )}
    </main>
  );
}